import {Injectable} from '@angular/core';
import {Router} from '@angular/router';

@Injectable({providedIn: 'root'})
export class PrintBuilderService {
  constructor(private router: Router) {
  }

  /**
   * Build a fully serialized URL string to the print route with required query params.
   * The path should match a print component route (e.g., '/transactions/invoice-print-ar').
   */
  buildPrintUrl(
    printRouteSegment: string,
    documentId: string,
    companyId: string,
    subLedger: string,
    extraQueryParams?: Record<string, any>
  ): string {
    const type = (subLedger || '').toString();
    const queryParams = {
      documentId,
      companyId,
      subLedger: type,
      type,
      ...(extraQueryParams || {})
    } as Record<string, any>;
    const tree = this.router.createUrlTree([`/transactions/${printRouteSegment}`], {
      queryParams
    });
    return this.router.serializeUrl(tree);
  }

  /**
   * Build HTML string for printing/emailing a transaction, branching by docType.
   * This is a minimal, shared rendering useful for emails or fallback printing.
   */
  buildPrintHtml(vm: any, docType: string): string {

    try {
      // Special handling by doc type
      const dt = (docType || '').toLowerCase();
      if (dt === 'receipt') {
        return this.buildReceiptHtml(vm);
      }
      if (dt === 'shipment') {
        return this.buildShipmentHtml(vm);
      }
      if (dt === 'workorder') {
        return this.buildWorkOrderHtml(vm);
      }
      const safe = (v: any) => (v === null || v === undefined ? '' : String(v));
      const header = vm?.item?.header || {};
      // Resolve the selected company by header.company_id. vm.company may be
      // a single object or an array of companies loaded for selects.
      const company = this.resolveCompany(vm?.company, header?.company_id);
      const term: any = this.resolveTerm(vm?.term, header?.term_id);
      const isQuote = dt === 'quotes';
      const isSO = dt === 'salesorders';
      const isPO = dt === 'purchaseorders';

      // Resolve addresses
      const billAddr = this.findAddress(vm?.billToAddress, header?.bill_address_id);
      const shipAddr = this.findAddress(vm?.shipToAddress, header?.ship_address_id);

      // Lines and totals
      const detail: any[] = Array.isArray(vm?.item?.detail) ? (vm.item.detail as any[]) : [];
      const normalizedLines = detail
        .filter((ln) => ln && ln.control_line === false)
        .map((ln) => {
          const qty = Number((isQuote || isSO || isPO) ? (ln.quantity ?? 0) : (ln.quantity || 0));
          const unit = Number(ln.amount || ln.unit_price || 0);
          const lineTotal = qty * unit;
          return {
            description: safe(ln.description || ln.itemInput || ''),
            quantity: qty,
            unitPrice: unit,
            total: lineTotal,
          };
        });

      // Subtotal from lines or vm.subtotal
      const subtotalFromLines = normalizedLines.reduce((acc, r) => acc + (Number.isFinite(r.total) ? r.total : 0), 0);
      const subtotal = Number(
        (vm?.subtotal ?? header?.subtotal ?? subtotalFromLines) || 0
      );

      // Tax total from vm or sum of detail tax amounts
      const taxFromDetails = (Array.isArray(detail) ? detail : []).reduce((acc, ln: any) => {
        return acc + Number(ln?.tax_amount_1 || 0) + Number(ln?.tax_amount_2 || 0) + Number(ln?.tax_amount_3 || 0);
      }, 0);
      const taxTotal = Number(vm?.taxtotal ?? header?.tax_total ?? taxFromDetails ?? 0);

      // Total from vm or subtotal + tax
      const total = Number(vm?.total ?? header?.total ?? subtotal + taxTotal ?? 0);

      // Paid from header or applied list
      let paid = Number(header?.payment_applied_total || 0);
      if (!paid && Array.isArray(vm?.item?.applied)) {
        paid = (vm.item.applied as any[]).reduce((acc, r: any) => acc + Number(r?.applied_amount || 0), 0);
      }
      const due = total - (paid || 0);

      const title = this.titleForDocType(docType);

      const lowerType = (docType || '').toLowerCase();
      const isCredit = lowerType === 'ar-credit' || lowerType === 'ap-credit';

      const showPaidDue = !isQuote && !isSO && !isPO;

      const styles = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color: #000; }
        .wrap { padding: 16px; }
        .hdr { display: flex; justify-content: space-between; align-items: flex-start; }
        .company { max-width: 60%; font-size: 13px; }
        .doc { text-align: right; font-size: 13px; }
        .docTitle { letter-spacing: 3px; font-size: 18px; font-weight: bold; }
        .section { margin-top: 16px; }
        .grid { width: 100%; border-collapse: collapse; }
        .grid th, .grid td { padding: 6px 8px; border-bottom: 1px solid #ccc; font-size: 12px; }
        .grid th { border-top: 1px solid #000; background: #f8f8f8; text-align: left; }
        .right { text-align: right; }
        .muted { color: #666; }
        .two-col { display: flex; gap: 24px; }
        .two-col .col { flex: 1; font-size: 12px; }
        .totals { margin-top: 12px; width: 100%; border-collapse: collapse; }
        .totals td { padding: 4px 8px; }
        .totals .label { text-align: right; }
        .totals .value { text-align: right; min-width: 60px; }
        .strong { font-weight: bold; }
        .headings { margin-top: 8px; font-size: 12px; }
        .headings td { padding: 4px; }
        .headings .label { color: #333; min-width: 110px; }
      </style>`;

      const addrBlock = (label: string, a?: any) => {
        if (!a) {
          return `<div class="col"><div class="strong">${label}</div><div class="muted">(none selected)</div></div>`;
        }
        const lines = [a.name, a.address_1, a.address_2, `${safe(a.city)} ${safe(a.state || '')} ${safe(a.zip || '')}`, a.phone]
          .filter(Boolean)
          .map((x) => `<div>${safe(x)}</div>`) // already string
          .join('');
        return `<div class="col"><div class="strong">${label}</div>${lines}</div>`;
      };

      const linesTable = normalizedLines.length
        ? `
        <table class="grid section">
          <thead>
            <tr>
              <th>Description</th>
              <th class="right">Qty</th>
              <th class="right">Unit</th>
              <th class="right">Line Total</th>
            </tr>
          </thead>
          <tbody>
            ${normalizedLines
          .map(
            (r) => `
                <tr>
                  <td>${safe(r.description)}</td>
                  <td class="right">${this.formatNumber(r.quantity)}</td>
                  <td class="right">${this.formatNumber(r.unitPrice)}</td>
                  <td class="right">${this.formatNumber(r.total)}</td>
                </tr>`
          )
          .join('')}
          </tbody>
        </table>`
        : '<div class="section muted">No line items</div>';

      const comments = safe(header?.comment || '');

      // Heading block: replicate key fields shown in the invoice-form Credit Memo print
      // We render this for AR/AP Credit Memos so list print matches form print headings.
      const showCreditHeadings = isCredit;
      const headingsTable = showCreditHeadings
        ? `
        <table class="headings section">
          <tr>
            <td class="label">Entry Date:</td>
            <td>${this.formatDateFriendly(header?.header_date)}</td>
          </tr>
          <tr>
            <td class="label">Number:</td>
            <td>${safe(header?.number)}</td>
          </tr>
          <tr>
            <td class="label">Amount:</td>
            <td>${this.formatNumber(total)}</td>
          </tr>
          <tr>
            <td class="label">Terms:</td>
            <td>${safe(((header as any)?.terms) || (term?.name) || (term?.description) || '')}</td>
          </tr>
        <tr>
          <td class="label">Due Date:</td>
          <td>${this.formatDateFriendly((header as any)?.due_date)}</td>
        </tr>
        </table>`
        : '';

      return `<!DOCTYPE html>
    <html lang=""><head><meta charset="utf-8"/>${styles}<title>${title}</title></head>
    <body>
      <div class="wrap">
        <div class="hdr">
          <div class="company">
            ${company?.logo ? `<div><img src="${safe(company.logo)}" alt="Company Logo" style="max-height:80px"/></div>` : ''}
            <div class="strong">${safe(company.name)}</div>
            <div>${safe(company.address_1)} ${safe(company.address_2)}</div>
            <div>${safe(company.city)} ${safe(company.state || '')} ${safe(company.zip)}</div>
            <div>${safe(company.phone)}</div>
          </div>
          <div class="doc">
            <div class="docTitle">${isCredit ? String(title).toUpperCase() : title}</div>
            ${isCredit
        ? `${headingsTable}`
        : `<div># ${safe(header.number)}</div>
                 <div>${this.formatDateFriendly(header.header_date)}</div>`}
          </div>
        </div>

        <div class="two-col section">
          ${addrBlock('Bill To', billAddr)}
          ${addrBlock('Ship To', shipAddr)}
        </div>

        ${linesTable}

        <table class="totals section">
          <tr><td class="label muted">Subtotal</td><td class="value">${this.formatNumber(subtotal)}</td></tr>
          <tr><td class="label muted">Tax</td><td class="value">${this.formatNumber(taxTotal)}</td></tr>
          <tr><td class="label strong">Total</td><td class="value strong">${this.formatNumber(total)}</td></tr>
          ${showPaidDue ? `
          <tr><td class="label muted">Paid</td><td class="value">${this.formatNumber(paid)}</td></tr>
          <tr><td class="label strong">Due</td><td class="value strong">${this.formatNumber(due)}</td></tr>
          ` : ''}
        </table>

        ${comments ? `<div class="section"><div class="strong">Comments</div><div>${comments}</div></div>` : ''}
      </div>
    </body></html>`;
    } catch (_e) {
      // Fallback to minimal output if anything unexpected happens
      const title = this.titleForDocType(docType);
      return `<!DOCTYPE html><html lang=""><head><meta charset="utf-8"/><title>${title}</title></head><body><div>Document</div></body></html>`;
    }
  }

  buildEmailHtml(vm: any, docType: string): string {
    return this.buildPrintHtml(vm, docType);
  }

  /**
   * Build a simple, print-safe Receipt document for invoice payments.
   * Expects vm.item.applied to contain applied payment rows with applied_amount and applied_date.
   */
  private buildReceiptHtml(vm: any): string {
    const safe = (v: any) => (v === null || v === undefined ? '' : String(v));
    const header = vm?.item?.header || {};
    const company = this.resolveCompany(vm?.company, header?.company_id);
    // Resolve Bill To: prefer selected id, else first available bill address
    let billAddr = this.findAddress(vm?.billToAddress, header?.bill_address_id);
    if (!billAddr) {
      try {
        const list = Array.isArray(vm?.billToAddress)
          ? vm.billToAddress
          : (vm?.billToAddress ? Object.values(vm.billToAddress) : []);
        if (Array.isArray(list) && list.length) {
          billAddr = list[0];
        }
      } catch { /* ignore */
      }
    }
    // payments list
    // console.log('receipt data:', vm?.item?.applied);
    const applied: any[] = Array.isArray(vm?.item?.applied) ? (vm.item.applied as any[]) : [];
    const rows = applied.map(r => ({
      date: r?.applied_date || r?.date || header?.header_date,
      amount: Number(r?.applied_amount || 0),
      reference: r?.number || r?.reference || r?.applied_header_id || ''
    }));
    const totalReceived = rows.reduce((acc, r) => acc + (Number.isFinite(r.amount) ? r.amount : 0), 0);
    // Totals for AR receipt summary
    const paymentAmount = (() => {
      if (vm?.totals && vm.totals.paymentAmount != null) {
        return Math.abs(Number(vm.totals.paymentAmount));
      }
      const d0 = vm?.item?.detail?.[0];
      return Math.abs(Number(d0?.amount || 0));
    })();
    const originalOwed = (() => {
      if (vm?.totals && vm.totals.originalOwed != null) {
        return Number(vm.totals.originalOwed) || 0;
      }
      // fallback unknown
      return 0;
    })();
    const totalApplied = (() => {
      if (vm?.totals && vm.totals.totalApplied != null) {
        return Math.abs(Number(vm.totals.totalApplied));
      }
      return Math.abs(Number(totalReceived));
    })();
    const remainderToAccount = paymentAmount - totalApplied;

    const styles = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color: #000; }
        .wrap { padding: 16px; }
        .hdr { display: flex; justify-content: space-between; align-items: flex-start; }
        .company { max-width: 60%; font-size: 13px; }
        .doc { text-align: right; font-size: 13px; }
        .section { margin-top: 16px; }
        .two-col { display: flex; gap: 24px; }
        .col { flex: 1; font-size: 12px; }
        .grid { width: 100%; border-collapse: collapse; }
        .grid thead th { border-bottom: 2px solid #000; padding: 6px 8px; font-size: 12px; text-align: left; }
        .grid tbody td { border-bottom: 1px solid #ccc; padding: 6px 8px; font-size: 12px; }
        .right { text-align: right; }
        .strong { font-weight: bold; }
        .muted { color: #666; }
        .totals { margin-top: 12px; width: 100%; border-collapse: collapse; }
        .totals td { padding: 4px 8px; }
        .totals .label { text-align: right; }
        .totals .value { text-align: right; min-width: 80px; }
      </style>`;

    const addrBlock = (a?: any) => {
      if (!a) {
        return '<div class="muted">(no billing address on file)</div>';
      }
      // Ensure we have a display name even if address record lacks one
      // const displayName = a.name || a.entity_name || header?.entityInput || header?.entity_name || '';
      const displayName = a.entity_name || '';
      const lines = [displayName, a.address_1, a.address_2, `${safe(a.city)} ${safe(a.state || '')} ${safe(a.zip || '')}`, a.phone]
        .filter(Boolean)
        .map((x) => `<div>${safe(x)}</div>`)
        .join('');
      return lines;
    };

    const paymentsTable = rows.length ? `
      <table class="grid section">
        <thead>
          <tr><th>Date</th><th>Reference</th><th class="right">Amount</th></tr>
        </thead>
        <tbody>
          ${rows.map(r => `
            <tr>
              <td>${this.formatDateFriendly(r.date)}</td>
              <td>${safe(r.reference)}</td>
              <td class="right">${this.formatNumber(r.amount)}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    ` : '<div class="section muted">No payments found</div>';

    return `<!DOCTYPE html>
    <html lang=""><head><meta charset="utf-8"/>${styles}<title>Receipt</title></head>
    <body>
      <div class="wrap">
        <div class="hdr">
          <div class="company">
            ${company?.logo ? `<div><img src="${safe(company.logo)}" alt="Company Logo" style="max-height:80px"/></div>` : ''}
            <div class="strong">${safe(company?.name || '')}</div>
            <div>${safe(company?.address_1 || '')} ${safe(company?.address_2 || '')}</div>
            <div>${safe(company?.city || '')} ${safe(company?.state || '')} ${safe(company?.zip || '')}</div>
            <div>${safe(company?.phone || '')}</div>
          </div>
          <div class="doc">
            <div class="strong">RECEIPT</div>
            <div>Payment # ${safe(header?.number || '')}</div>
            <div>${this.formatDateFriendly(header?.header_date)}</div>
          </div>
        </div>

        <div class="section">
          <div class="strong">Bill To</div>
          ${addrBlock(billAddr)}
        </div>

        ${paymentsTable}

        <table class="totals section">
          <tr><td class="label strong">Total Payment</td><td class="value">${this.formatNumber(paymentAmount)}</td></tr>
          <tr><td class="label strong">Less Invoices Applied</td><td class="value">${this.formatNumber(totalApplied)}</td></tr>
          <tr><td class="label strong">Remaining Payment on Account</td><td class="value strong">${this.formatNumber(remainderToAccount)}</td></tr>
        </table>
      </div>
    </body></html>`;
  }

  private titleForDocType(docType: string): string {
    switch ((docType || '').toLowerCase()) {
      case 'ar-credit':
        return 'Credit Memo';
      case 'ap-credit':
        return 'Payable Credit Memo';
      case 'ar':
        return 'Invoice';
      case 'ap':
        return 'Payable Invoice';
      case 'quotes':
        return 'Quote';
      case 'salesorders':
        return 'Sales Order';
      case 'purchaseorders':
        return 'Purchase Order';
      case 'shipment':
        return 'Shipment';
      case 'workorder':
        return 'Work Order';
      default:
        return 'Document';
    }
  }

  private formatNumber(n: any): string {
    const num = Number(n || 0);
    return num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  /**
   * Format a date value into a friendly, locale-aware string for print/email.
   * Falls back gracefully to the raw value if parsing fails.
   */
  private formatDateFriendly(value: any): string {
    try {
      if (!value) {
        return '';
      }
      const d = new Date(value);
      if (isNaN(d.getTime())) {
        return String(value);
      }
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return String(value ?? '');
    }
  }

  private findAddress(list: any, id: any): any | undefined {
    if (!list || !id) {
      return undefined;
    }
    try {
      // list can be array or object map; normalize to array of values
      const arr = Array.isArray(list) ? list : Object.values(list);
      // ids may be string/number; compare loosely
      return (arr as any[]).find((a: any) => (a?.id == id));
    } catch {
      return undefined;
    }
  }

  /**
   * Resolve the selected company object from an input that may be a single
   * object or an array. Falls back to the input itself if no id match.
   */
  private resolveCompany(src: any, id: any): any {
    if (!src) {
      return {};
    }
    try {
      // If src already looks like a single company object with a name, return it
      if (!Array.isArray(src) && typeof src === 'object' && (src as any).name) {
        return src;
      }
      const list = Array.isArray(src) ? src : Object.values(src);
      if (id != null) {
        const found = (list as any[]).find((c: any) => c?.id == id);
        if (found) {
          return found;
        }
      }
      // No id or not found: best effort pick first with a name
      const first = (list as any[]).find((c: any) => !!c && (c.name || c.logo || c.address_1));
      return first || {};
    } catch {
      return {};
    }
  }

  /**
   * Resolve a Term object from a source that may be a single item, an array, or a map.
   * Matches by id when provided; otherwise returns the first item with a recognizable
   * name/description. If src is already a string, return an object with name set.
   */
  private resolveTerm(src: any, id: any): any {
    try {
      if (!src && (id === undefined || id === null)) {
        return {};
      }
      // If src is a string, wrap it as a term-like object
      if (typeof src === 'string') {
        return {name: src};
      }
      // If src already looks like a single term object
      if (src && !Array.isArray(src) && typeof src === 'object' && ((src as any).name || (src as any).description)) {
        return src;
      }
      // Normalize to array of items from array or object map
      const list = Array.isArray(src) ? src : (src ? Object.values(src) : []);
      if (id != null) {
        const found = (list as any[]).find((t: any) => (t?.id == id) || (t?.term_id == id));
        if (found) {
          return found;
        }
      }
      // Fallback: first with a name/description
      const first = (list as any[]).find((t: any) => !!t && ((t.name) || (t.description)));
      return first || {};
    } catch {
      return {};
    }
  }

  /**
   * Build a Work Order specific HTML using the legacy work-orders-print layout as guidance.
   * Expects a VM shaped by mapWorkOrderToPrintVm() from the WorkOrdersFormComponent.
   */
  private buildWorkOrderHtml(vm: any): string {
    const safe = (v: any) => (v === null || v === undefined ? '' : String(v));
    const header = vm?.item?.wo_header || vm?.item?.header || {};
    const company = this.resolveCompany(vm?.company, header?.company_id);
    const billAddr = this.findAddress(vm?.billToAddress, header?.bill_address_id) || {};
    const shipAddr = this.findAddress(vm?.shipToAddress, header?.ship_address_id) || {};
    const users: any[] = Array.isArray(vm?.user) ? vm.user : [];

    const wo_items: any[] = Array.isArray(vm?.item?.wo_items) ? vm.item.wo_items : [];
    const wo_hours: any[] = Array.isArray(vm?.item?.wo_hours) ? vm.item.wo_hours : [];
    const wo_travel: any[] = Array.isArray(vm?.item?.wo_travel) ? vm.item.wo_travel : [];
    const wo_notes: any[] = Array.isArray(vm?.item?.wo_notes) ? vm.item.wo_notes : [];
    const wo_attachments: any[] = Array.isArray(vm?.item?.wo_attachments) ? vm.item.wo_attachments : [];

    // Compose printItem-like fields from inputs
    const printItem: any = {};
    printItem.companyLogo = company?.logo || '';
    printItem.companyName = company?.name || '';
    printItem.companyAddress1 = [company?.address_1, company?.address_2].filter(Boolean).join(', ');
    printItem.companyAddress2 = [company?.city, company?.state].filter(Boolean).join(', ');
    if (company?.zip) {
      printItem.companyAddress2 += (printItem.companyAddress2 ? '  ' : '') + company.zip;
    }
    printItem.companyTaxNumber = company?.tax_number || '';
    printItem.companyPhone = company?.phone || '';

    // Ship to address: prefer shipAddr; fallback to billAddr
    const ship = (shipAddr && shipAddr.address_1) ? shipAddr : billAddr;
    const shipToAddress1 = [ship?.address_1, ship?.address_2].filter(Boolean).join(', ');
    let shipToAddress2 = [ship?.city, ship?.state].filter(Boolean).join(', ');
    if (ship?.zip) {
      shipToAddress2 += (shipToAddress2 ? '  ' : '') + ship.zip;
    }

    const techName = (() => {
      const u = users.find((x: any) => x?.id == header?.assigned_user_id);
      return u?.name || '';
    })();

    const woTypes: any[] = Array.isArray(vm?.woTypes) ? vm.woTypes : [];
    const woCategories: any[] = Array.isArray(vm?.woCategories) ? vm.woCategories : [];
    const woTypeName = woTypes.find((t: any) => t.id == header?.type_id)?.name || '';
    const woCategoryName = woCategories.find((c: any) => c.id == header?.category_id)?.name || '';

    // Entity info from VM (passed by buildPrintVm)
    const entity = vm?.entity || null;
    const entityAddress = vm?.address || null;
    const entityContact = vm?.contact || null;
    const entityName = entity?.name || header?.entityInput || '';
    const entityPhone = entity?.phone || '';
    const entityEmail = entity?.email || '';
    const entityAddr1 = entityAddress
      ? [entityAddress.address_1, entityAddress.address_2].filter(Boolean).join(', ')
      : shipToAddress1;
    const entityAddr2 = entityAddress
      ? [entityAddress.city, entityAddress.state].filter(Boolean).join(', ') + (entityAddress.zip ? '  ' + entityAddress.zip : '')
      : shipToAddress2;
    const contactName = entityContact
      ? [entityContact.first_name ?? entityContact.name, entityContact.last_name].filter(Boolean).join(' ')
      : '';
    const contactPhone = entityContact?.phone || '';

    // Utility: format date strings like Angular template expects (use toLocaleDateString)
    const fmtDate = (d: any, style: 'short' | 'medium' | 'long' | 'none' = 'short') => {
      if (!d) {
        return '';
      }
      try {
        const dt = new Date(d);
        if (isNaN(dt.getTime())) {
          return String(d);
        }
        if (style === 'medium') {
          return dt.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: '2-digit'});
        }
        return dt.toLocaleDateString();
      } catch {
        return String(d);
      }
    };

    // Build tables as in the provided template
    const itemsTable = () => {
      if (!wo_items.length) {
        return '';
      }
      const resolveUom = (id: any) => {
        try {
          if (!id) {
            return '';
          }
          const arr = Array.isArray((vm as any)?.uom) ? (vm as any).uom : [];
          const found = arr.find((u: any) => u?.id == id);
          return found?.code || found?.uom_code || found?.name || '';
        } catch {
          return '';
        }
      };
      return `<table class="invoice-detail wo-items">
        <thead>
          <tr>
            <th class="w1">Item</th>
            <th class="text-center">Description</th>
            <th class="text-center w5">Qty</th>
            <th class="text-center w8">UOM</th>
          </tr>
        </thead>
        <tbody>
          ${wo_items.map((d: any) => {
        const hasAny = (d && (d.description || d.itemInput || d.item || d.item_code || d.code || d.number || d.item_number || d.quantity != null || d.uom_code || d.uom_id));
        if (!hasAny) {
          return '';
        }
        // Show an item number/code, never a database id
        const code = d.itemInput || d.item_code || d.code || d.number || d.item_number || d.item || '';
        const desc = d.description || d.item_description || d.notes || d.comment || '';
        const uom = d.uom_code || resolveUom(d.uom_id);
        return `<tr>
              <td class="code nowrap">${safe(code)}</td>
              <td class="text-center">${safe(desc)}</td>
              <td class="text-center">${safe(d.quantity ?? '')}</td>
              <td class="text-center nowrap">${safe(uom)}</td>
            </tr>`;
      }).join('')}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4">&nbsp;</td>
          </tr>
        </tfoot>
      </table>`;
    };

    const hoursTable = () => {
      if (!wo_hours.length) {
        return '';
      }
      return `<table class="invoice-detail">
        <thead>
          <tr><th>Tech</th><th class="text-center w1">Hrs</th><th class="text-center w1 nowrap">Date</th><th>Description</th></tr>
        </thead>
        <tbody>
          ${wo_hours.map((d: any) => (d?.user_id ? `<tr>
            <td class="nowrap">${safe((users.find(u => u.id == d.user_id) || {}).name || '')}</td>
            <td class="text-center">${safe(d.hours ?? '')}</td>
            <td class="text-center nowrap">${fmtDate(d.date, 'medium')}</td>
            <td style="white-space: pre-wrap">${safe(d.description || d.notes || '')}</td>
          </tr>` : '')).join('')}
        </tbody>
        <tfoot>
          <tr><td colspan="4">&nbsp;</td></tr>
        </tfoot>
      </table>`;
    };

    const travelTable = () => {
      if (!wo_travel.length) {
        return '';
      }
      return `<table class="invoice-detail">
        <thead>
          <tr><th>Tech</th><th class="text-center w1">KM</th><th class="text-center w1 nowrap">Date</th><th>Description</th></tr>
        </thead>
        <tbody>
          ${wo_travel.map((d: any) => (d?.user_id ? `<tr>
            <td class="nowrap">${safe((users.find(u => u.id == d.user_id) || {}).name || '')}</td>
            <td class="text-center">${safe(d.distance ?? '')}</td>
            <td class="text-center nowrap">${fmtDate(d.date, 'medium')}</td>
            <td style="white-space: pre-wrap">${safe(d.description || d.notes || '')}</td>
          </tr>` : '')).join('')}
        </tbody>
        <tfoot>
          <tr><td colspan="4">&nbsp;</td></tr>
        </tfoot>
      </table>`;
    };

    const siteNotesTable = (limitToFirstTwo: boolean) => {
      if (!wo_notes.length) {
        return '';
      }
      const rows = limitToFirstTwo ? wo_notes.slice(0, 2) : wo_notes;
      return `<table class="invoice-detail">
        <thead>
          <tr><th class="text-center w1 nowrap">Date</th><th>Site Notes&nbsp;</th></tr>
        </thead>
        <tbody>
          ${rows.map((d: any) => (d?.note || d?.notes) ? `<tr>
            <td class="text-center nowrap">${fmtDate(d.date)}</td>
            <td style="white-space: pre-wrap">${safe(d.note || d.notes || '')}</td>
          </tr>` : '').join('')}
          ${limitToFirstTwo && wo_notes.length > 2 ? `<tr><td colspan="2">Please see page 2 for site notes...</td></tr>` : ''}
        </tbody>
        <tfoot>
          <tr><td colspan="2">&nbsp;</td></tr>
        </tfoot>
      </table>`;
    };

    // Emergency banner
    const emergencyBanner = header?.status === 'Emergency' ? `<div style="border:2px solid #000;padding:20px;font-size:140%;font-weight:bold;text-align:center;margin-bottom:20px;" class="text-danger">E M E R G E N C Y</div>` : '';

    // First page header and top block
    const topHtml = `
      <table style="width:100%" cellpadding="0" cellspacing="0"><tr><td style="vertical-align:top">
        <div>
          ${printItem.companyLogo ? `<img class="mb-1" style="height:100px" src="${safe(printItem.companyLogo)}" /><br/>` : ''}
          ${safe(printItem.companyName)} <br/>
          ${safe(printItem.companyAddress1)} <br/>
          ${safe(printItem.companyAddress2)}
          ${company?.address_3 ? `<br/>${safe(company.address_3)}` : ''}
          ${printItem.companyTaxNumber ? `<br/><small>Tax: ${safe(printItem.companyTaxNumber)}</small>` : ''}
          ${printItem.companyPhone ? `<div>${safe(printItem.companyPhone)}</div>` : ''}
        </div>
        <div style="height:16px"></div>
        <table style="width:100%" cellpadding="0" cellspacing="0"><tr>
          <td style="min-width:200px;vertical-align:text-top;width:40%;padding-right:20px;">
            <div style="font-size:120%"><b class="nowrap">${safe(entityName)}</b><br/>
              ${entityPhone ? `<span style="font-size:85%">${safe(entityPhone)}</span><br/>` : ''}
              ${entityEmail ? `<span style="font-size:85%">${safe(entityEmail)}</span><br/>` : ''}
              ${safe(entityAddr1)}${entityAddr1 ? '<br/>' : ''}${safe(entityAddr2)}
              ${contactName ? `<br/><span style="font-size:85%">Contact: ${safe(contactName)}${contactPhone ? ' · ' + safe(contactPhone) : ''}</span>` : ''}
            </div>
          </td>
        </tr></table>
      </td>
      <td style="vertical-align:top">
        <table class="invoice-number-table nowrap">
          <tr><td colspan="2" class="text-bold text-center"><h1><div style="letter-spacing:8px">WORK</div><div style="letter-spacing:8px">ORDER</div></h1></td></tr>
          <tr><td>Number:&nbsp;</td><td class="text-bold hdr-value">${safe(header.number)}</td></tr>
          <tr><td>Date:&nbsp;</td><td class="text-bold hdr-value">${safe(this.formatDateFriendly(header.work_order_date || header.header_date || ''))}</td></tr>
          ${(wo_notes.length > 2 || wo_attachments.length > 0) ? `<tr><td>Page:&nbsp;</td><td class="text-bold">${(wo_attachments.length === 0 && wo_notes.length > 2) || (wo_attachments.length > 0 && wo_notes.length < 3) ? '1 of 2' : (wo_attachments.length > 0 && wo_notes.length > 2 ? '1 of 3' : '')}</td></tr>` : ''}
        </table>
      </td></tr></table>
      ${(header.description || header.comment) ? `<div style="margin-top:10px;white-space:pre-wrap"><strong>Description:</strong> ${safe(header.description || header.comment || '')}</div>` : ''}
      <div style="margin-top:14px;padding:10px 14px;background:#f5f7fa;border:1px solid #d0d7e2;border-radius:6px;">
        <table style="width:100%;border-collapse:collapse" cellpadding="0" cellspacing="0"><tr>
          <td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Status</div>
            <div style="font-weight:600">${safe(header.status || '—')}</div>
          </td>
          <td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Priority</div>
            <div style="font-weight:600">${safe(header.priority || '—')}</div>
          </td>
          ${woTypeName ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Type</div>
            <div style="font-weight:600">${safe(woTypeName)}</div>
          </td>` : ''}
          ${woCategoryName ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Category</div>
            <div style="font-weight:600">${safe(woCategoryName)}</div>
          </td>` : ''}
          ${techName ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Technician</div>
            <div style="font-weight:600">${safe(techName)}</div>
          </td>` : ''}
          ${header?.required_by_date ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Required By</div>
            <div style="font-weight:600">${safe(this.formatDateFriendly(header.required_by_date))}</div>
          </td>` : ''}
          ${header?.estimated_hours ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Est. Hours</div>
            <div style="font-weight:600">${safe(header.estimated_hours)}</div>
          </td>` : ''}
          ${header?.estimated_cost ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Est. Cost</div>
            <div style="font-weight:600">$${safe(Number(header.estimated_cost).toFixed(2))}</div>
          </td>` : ''}
          ${header?.completed_date ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Completed</div>
            <div style="font-weight:600">${safe(this.formatDateFriendly(header.completed_date))}</div>
          </td>` : ''}
          ${header?.next_visit_date && !header?.completed_date ? `<td style="padding:0 18px 0 0;white-space:nowrap;vertical-align:top">
            <div style="font-size:75%;text-transform:uppercase;color:#6c757d;letter-spacing:.05em;margin-bottom:2px">Next Visit</div>
            <div style="font-weight:600">${safe(this.formatDateFriendly(header.next_visit_date))}</div>
          </td>` : ''}
        </tr></table>
      </div>`;

    const emptyGrids = (!wo_items.length && !wo_hours.length && !wo_travel.length && !wo_notes.length) ? `
      <table cellpadding="0" cellspacing="0" style="width:100%"><tr>
        <td>
          <table class="invoice-detail">
            <thead><tr><th>Items/Parts</th><th class="w1">Qty</th></tr></thead>
            <tbody>
              <tr><td colspan="2">&nbsp;</td></tr><tr><td colspan="2">&nbsp;</td></tr><tr><td colspan="2">&nbsp;</td></tr><tr><td colspan="2">&nbsp;</td></tr>
            </tbody>
            <tfoot><tr><td colspan="2">&nbsp;</td></tr></tfoot>
          </table>
        </td>
        <td class="gutter">&nbsp;</td>
        <td class="gutter" style="border-left:1px solid #000">&nbsp;</td>
        <td>
          <table class="invoice-detail">
            <thead><tr><th>Tech Hours</th><th class="text-center w1">Hrs</th><th class="text-center w1">Date</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td colspan="4">&nbsp;</td></tr><tr><td colspan="4">&nbsp;</td></tr><tr><td colspan="4">&nbsp;</td></tr><tr><td colspan="4">&nbsp;</td></tr>
            </tbody>
            <tfoot><tr><td colspan="4">&nbsp;</td></tr></tfoot>
          </table>
        </td>
      </tr></table>
      <div style="height:10px"></div>
      <table class="invoice-detail" style="width:100%">
        <thead><tr><th>Tech Travel</th><th class="text-center w1">KM</th><th class="text-center w1">Date</th><th>Notes</th></tr></thead>
        <tbody>
          <tr><td colspan="4">&nbsp;</td></tr><tr><td colspan="4">&nbsp;</td></tr><tr><td colspan="4">&nbsp;</td></tr><tr><td colspan="4">&nbsp;</td></tr>
        </tbody>
        <tfoot><tr><td colspan="4">&nbsp;</td></tr></tfoot>
      </table>` : '';

    // Side-by-side items/hours
    const itemsHours = `<table cellpadding="0" cellspacing="0" style="width:100%"><tr>
      ${wo_items.length ? `<td ${wo_hours.length ? 'class="w50"' : ''}>${itemsTable()}</td>` : ''}
      ${(wo_items.length && wo_hours.length) ? '<td class="gutter">&nbsp;</td><td class="gutter" style="border-left:1px solid #000">&nbsp;</td>' : ''}
      ${wo_hours.length ? `<td>${hoursTable()}</td>` : ''}
    </tr></table>`;

    // Travel and site notes section
    const travelNotes = `<table cellpadding="0" cellspacing="0" style="width:100%"><tr>
      ${wo_travel.length ? `<td ${wo_notes.length ? 'class="w50"' : ''}>${travelTable()}</td>` : ''}
      ${(wo_travel.length && wo_notes.length) ? '<td class="gutter">&nbsp;</td><td class="gutter" style="border-left:1px solid #000">&nbsp;</td>' : ''}
      ${wo_notes.length ? `<td>${siteNotesTable(true)}</td>` : ''}
    </tr></table>`;

    // Signature image rendered as a regular block to avoid overlap, with section title
    const signatureImg = header?.signature ? `<div class="signature-block">
        <div class="signature-title">Customer Signature</div>
        <img style="max-height:200px; max-width:100%" src="${safe(header.signature)}"/>
      </div>` : '';

    // Page 2 for site notes if > 2
    const secondPageNotes = wo_notes.length ? `
      <div class="d-print-block" style="page-break-before:always">
        <p class="text-right">
          ${wo_attachments.length ? `<span>${wo_notes.length < 3 ? 'Page: 2 of 2' : 'Page: 2 of 3'}</span>` : '<span>Page: 2 of 2</span>'}
        </p>
        <table class="invoice-detail">
          <thead>
            <tr><th class="text-center w1 nowrap">Date</th><th>Site notes for Work Order number: &nbsp;<span class="text-bold code">${safe(header.number)}</span></th></tr>
          </thead>
          <tbody>
            ${wo_notes.map((d: any) => (d?.note || d?.notes) ? `<tr>
              <td class="text-center nowrap">${fmtDate(d.date, 'short')}</td>
              <td style="white-space: pre-wrap">${safe(d.note || d.notes || '')}</td>
            </tr>` : '').join('')}
          </tbody>
          <tfoot>
            <tr><td colspan="2">&nbsp;</td></tr>
          </tfoot>
        </table>
      </div>` : '';

    // Attachments page if present
    const attachmentsPage = wo_attachments.length ? `
      <div class="d-print-block" style="page-break-before:always">
        <span class="float-end">${wo_notes.length < 3 ? 'Page: 2 of 2' : 'Page: 3 of 3'}</span>
        <strong>Photos For Work Order Number: &nbsp;<span class="text-bold code">${safe(header.number)}</span></strong>
        <hr/>
        <div>
          ${wo_attachments.map((a: any) => `<div style="display:inline-block; max-width:48%; margin:0 2% 20px 0"><img src="${safe(a.attachment || a.url || '')}" style="max-width:100%"/></div>`).join('')}
        </div>
      </div>` : '';

    const styles = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color:#000; }
        .invoice-detail { width:100%; border-collapse: collapse; page-break-inside: avoid; table-layout: auto; }
        .invoice-detail th, .invoice-detail td { border-bottom:1px solid #000; padding:6px 8px; font-size:12px; vertical-align: top; }
        /* Consistent header/footer borders across all tables */
        .invoice-detail thead th { border-bottom: 2px solid #000; }
        .invoice-detail tfoot td { border-top: 2px solid #000; border-bottom: 0; padding: 0; height: 6px; }
        .invoice-number-table { width:100%; }
        /* Uniform header info value size */
        .hdr-value { font-size: 120% !important; }
        .nowrap { white-space: nowrap; }
        .text-center { text-align:center; }
        .text-right { text-align:right; }
        .text-bold { font-weight:bold; }
        .code { font-family: monospace; }
        /* Data column narrow class widened to reduce wrapping */
        .w1 { width:12%; }
        /* Items table specific narrow columns: widen to match .w1 to avoid wrapping */
        .w5 { width:12%; }
        .w8 { width:12%; }
        .w50 { width:50%; }
        /* Very narrow gutter/separator between side-by-side tables */
        .gutter { width:1%; }
        .detail-wrapper { /* removed relative to prevent positioned overlap */ }
        .signature-block { margin-top: 16px; page-break-inside: avoid; }
        .signature-title { font-weight: bold; margin-bottom: 6px; }
        img { max-width: 100%; height: auto; }
        .spacer { height: 8px; }
        /* help long words wrap to avoid overflow collisions */
        td, th { word-break: break-word; overflow-wrap: anywhere; }
        /* Items table keeps same consistent rules (redundant but safe) */
        .invoice-detail.wo-items thead th { border-bottom: 2px solid #000; }
        .invoice-detail.wo-items tfoot td { border-top: 2px solid #000; border-bottom: 0; padding: 0; height: 6px; }
      </style>`;

    const firstPage = `
      <div id="wo-print-root" class="print-invoice">
        ${topHtml}
        <div style="height: 20px"></div>
        <div class="detail-wrapper">
          ${emergencyBanner}
          ${emptyGrids || ''}
          ${itemsHours}
          <div>&nbsp;</div>
          ${travelNotes}
          ${signatureImg}
        </div>
      </div>`;

    return `<!DOCTYPE html><html lang=""><head><meta charset="utf-8"/><title>Work Order</title>${styles}</head><body>
      ${firstPage}
      ${wo_notes.length > 2 ? secondPageNotes : ''}
      ${wo_attachments.length ? attachmentsPage : ''}
    </body></html>`;
  }

  /**
   * Build HTML for Shipment using the existing shipment-print template as the basis.
   * We do not execute Angular bindings; instead we render from the provided vm.
   */
  private buildShipmentHtml(vm: any): string {
    const safe = (v: any) => (v === null || v === undefined ? '' : String(v));
    const header = vm?.item?.header || {};
    const print = vm?.printItem || {};
    const sub = (vm?.subLedger || '').toLowerCase(); // 'ar' sales or 'ap' receiving

    // Normalize detail array
    const detail: any[] = Array.isArray(vm?.item?.detail) ? vm.item.detail : [];
    const inventory: any[] = Array.isArray(vm?.inventoryItem) ? vm.inventoryItem : [];
    // Order by sort_order like template does (ascending)
    const lines = [...detail]
      .filter((d) => !!d)
      .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0));

    const styles = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color:#000; }
        .d-print-block { display:block; }
        .wrap { padding: 16px; }
        .invoice-number-table { margin-left:auto; border-collapse:collapse; }
        .invoice-number-table td { padding:4px; border-bottom:1px solid #000; }
        /* Use min-height to avoid clipping long addresses; allow content to grow */
        .company-wrapper { min-height:170px; height:auto; font-size:100%; overflow:visible; margin-bottom:20px; }
        .invoice-header-table { width:100%; }
        .invoice-header-table td { vertical-align: top; }
        .detail-wrapper { min-height: 300px; overflow:visible; }
        .page-break { page-break-before: always; }
        .tdg { width:100%; border-top:1px solid #000; border-left:1px solid #000; border-collapse: collapse; }
        .tdg td, .tdg th { border-bottom:1px solid #000; border-right:1px solid #000; padding:4px; }
        .nowrap { white-space: nowrap; }
        .invoice-detail { width:100%; border-collapse: collapse; }
        .invoice-detail th { border-top:1px solid #000; border-bottom:2px solid #000; padding:3px 2px; white-space:nowrap; text-align:left; }
        .invoice-detail td { border-bottom:1px solid #000; padding:3px 2px; }
        .text-center { text-align:center; }
        .text-bold { font-weight:700; }
        .code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
        .w1 { width: 140px; }
        .w5 { width: 80px; }
        .w8 { width: 110px; }
      </style>`;

    const companyBlock = `
      <div class="company-wrapper">
        ${print.companyLogo ? `<img class="mb-1" style="height:100px" src="${safe(print.companyLogo)}"/>` : ''}<br/>
        ${safe(print.companyName)}<br/>
        ${safe(print.companyAddress1)}<br/>
        ${safe(print.companyAddress2)}
        ${print.companyAddress3 ? `<br/>${safe(print.companyAddress3)}` : ''}
        ${print.companyPhone ? `<br/><small>Phone: ${safe(print.companyPhone)}</small>` : ''}
      </div>`;

    const shipOrBillLeft = sub !== 'ap'
      ? `<div>
           Ship To:<br/>
           ${safe(header.entityInput)}<br/>
           ${safe(print.shipToAddress1)}<br/>
           ${safe(print.shipToAddress2)}
           ${!print.shipToAddress1 ? `<br/>${safe(print.billToAddress1)}<br/>${safe(print.billToAddress2)}` : ''}
         </div>
         <table width="100%"><tr><td style="min-width:200px"></td></tr></table>`
      : `<div class="company-wrapper">
           ${safe(header.entityInput)}<br/>
           ${safe(print.billToAddress1)}<br/>
           ${safe(print.billToAddress2)}
         </div>
         Bill To:<br/>
         ${safe(print.companyName)}<br/>
         ${safe(print.companyAddress1)}<br/>
         ${safe(print.companyAddress2)}`;

    // Resolve an order number from multiple possible sources to ensure it prints
    const resolvedOrderNumber = safe(
      (vm as any)?.orderNumber ||
      (vm as any)?.order?.header?.number ||
      (vm as any)?.item?.order?.header?.number ||
      header?.linked_header_number ||
      header?.order_number ||
      ''
    );

    const headingRight = `
      <table class="invoice-number-table">
        <tr>
          <td colspan="2" class="text-bold text-center">
            <h1>
              ${sub === 'ap' ? '&nbsp;RECEIVING<br/>' : sub === 'ar' ? 'SALES<br/>' : ''}
              SHIPMENT
            </h1>
          </td>
        </tr>
        <tr><td>Number:&nbsp;</td><td class="text-bold code">${safe(header.number)}</td></tr>
        <tr><td>Date:&nbsp;</td><td class="text-bold">${this.formatDateFriendly(header.header_date)}</td></tr>
        <tr><td>Order:&nbsp;</td><td class="code">${resolvedOrderNumber}</td></tr>
        ${header.ref ? `<tr><td>PO/Ref:&nbsp;</td><td class="text-bold code">${safe(header.ref)}</td></tr>` : ''}
      </table>`;

    const headerTable = `
      <table class="invoice-header-table"><tr>
        <td>${sub !== 'ap' ? companyBlock : ''}${sub !== 'ap' ? shipOrBillLeft : ''}${sub === 'ap' ? '' : ''}</td>
        ${sub === 'ap' ? `<td>${shipOrBillLeft}</td>` : ''}
        <td>${headingRight}</td>
      </tr></table>`;

    const description = safe(header.description || '');

    const linesHtml = lines
      .filter((d) => !!d.description)
      .map((d) => `
        <tr>
          <td class="code">${safe(d.itemInput || '')}</td>
          <td>${safe(d.description || '')}</td>
          <td class="text-center">${safe(d.shipped_quantity ?? d.quantity ?? '')}</td>
          <td class="text-center">${safe(d.uom || '')}</td>
        </tr>`)
      .join('');

    const tableHtml = `
      <div class="detail-wrapper">
        <table class="invoice-detail">
          <tr>
            <th class="w1">Item&nbsp;</th>
            <th>Description</th>
            <th class="text-center w5">Qty</th>
            <th class="text-center w8">UOM</th>
          </tr>
          ${linesHtml || ''}
        </table>
      </div>`;

    // Build page 2: SHIPPING DOCUMENT with TDG tables and signatures
    const formatMDY = (value: any): string => {
      try {
        const d = new Date(value);
        if (isNaN(d.getTime())) {
          return safe(value);
        }
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
      } catch {
        return safe(value);
      }
    };

    // Map details to inventory once
    const mapToInventory = (d: any) => inventory.find((it: any) => (it?.id == d?.item_id));
    const dangerousRows = lines
      .map((d) => ({d, i: mapToInventory(d)}))
      .filter(({d, i}) => !!d?.item_id && i && !!i.UNNumber)
      .map(({d, i}) => `
        <tr>
          <td>${safe(i.UNNumber)}</td>
          <td>${safe(i.ShippingName)}</td>
          <td>${safe(i.PrimaryClass)}</td>
          <td>${safe(i.SubsidiaryClass)}</td>
          <td>${safe(i.PackingGroup)}</td>
          <td>${safe(i.Toxic)}</td>
          <td>${safe(i.weight)}</td>
          <td>${safe(d.quantity)}</td>
        </tr>`)
      .join('');

    const nonRegRows = lines
      .map((d) => ({d, i: mapToInventory(d)}))
      .filter(({d, i}) => !!d?.item_id && i && !i.UNNumber)
      .map(({d, i}) => `
        <tr>
          <td>${safe(d.quantity)}</td>
          <td>${safe(d.description)}</td>
          <td>${safe(i.weight)}</td>
        </tr>`)
      .join('');

    const page2Html = `
      <div class="page-break text-center text-bold">SHIPPING DOCUMENT</div>
      <div>Date: ${formatMDY(header?.header_date)}</div>
      <div>&nbsp;</div>
      <table style="width:100%"><tr>
        <td style="width:20%">Shipper</td>
        <td style="width:30%">
          ${safe(print.companyName)}<br/>
          ${safe(print.companyAddress1)}<br/>
          ${safe(print.companyAddress2)}
          ${print.companyAddress3 ? `<br/>${safe(print.companyAddress3)}` : ''}
          ${print.companyPhone ? `<br/>${safe(print.companyPhone)}` : ''}
        </td>
        <td style="width:20%">Receiver</td>
        <td style="width:30%">
          Ship To:<br/>
          ${safe(header.entityInput)}<br/>
          ${safe(print.shipToAddress1)}<br/>
          ${safe(print.shipToAddress2)}
          ${!print.shipToAddress1 ? `<br/>${safe(print.billToAddress1)}<br/>${safe(print.billToAddress2)}` : ''}
        </td>
      </tr></table>
      <div>&nbsp;</div>
      <div>
        Name of Courier: _____________________________
        Shipping Doc # _________________
      </div>
      <div>&nbsp;</div>
      <div>Transport Unit: _____________________________</div>
      <div>&nbsp;</div>
      <div class="text-center" style="border:1px solid #000; padding:4px; font-weight:bold">
        REGULATED DANGEROUS GOODS<br/>
        24 HOUR EMERGENCY PHONE NUMBER (613) 996-6666
      </div>
      <div>&nbsp;</div>
      <table class="tdg" cellpadding="0" cellspacing="0" style="width:100%; border:1px solid #000">
        <tr class="nowrap">
          <th>UN Number</th>
          <th>Shipping Name<br/><small>(If applicable, Technical Name)</small></th>
          <th class="text-center">Primary<br/>Class</th>
          <th class="text-center">Subsidiary<br/>Class</th>
          <th class="text-center">Packing<br/>Group</th>
          <th>Toxic by<br/>inhalation<br/>(SP 23)</th>
          <th class="text-center">Total<br/>Quantity<br/>(kg or L)</th>
          <th class="text-center">Number of<br/>packages<br/>requiring<br/>labels</th>
        </tr>
        ${dangerousRows}
      </table>
      <p>
        I hereby declare that the contents of this consigment are fully and accurately described above by the proper shipping name, are properly classified and packaged, have dangerous goods safety marks properly affixed or displayed on them, and are of all respects of proper condition for transport according to the Transportation of Dangerouds Goods Regulations.
      </p>
      <p>
        _____________________________________________________________________ Shipper's name (please print)
      </p>
      ${nonRegRows ? `
      <div class="text-center" style="border:1px solid #000; padding:4px; font-weight:bold">Non Regulated Dangerous Goods</div>
      <table class="tdg">
        <tr>
          <th>Packages</th>
          <th>Description of articles</th>
          <th>Weight</th>
        </tr>
        ${nonRegRows}
      </table>` : ''}
      <div>&nbsp;</div>
      <table class="tdg"><tbody>
        <tr>
          <td colspan="2" style="border-bottom:1px solid #000; border-right:1px solid #000">
            Received of apparent good order:: <br/><br/>
            ______________________________________<br/>
            <small>Consignee's signature</small>
          </td>
          <td style="border-bottom:1px solid #000">
            Driver's #_____________________ <br/><br/>
            __________________________<br/>
            <small>Driver's Signature:</small>
          </td>
        </tr>
      </tbody></table>`;

    // Optional Images page (page 3) if attachments exist
    const attachments: any[] = Array.isArray(vm?.item?.attachments) ? [...vm.item.attachments] : [];
    attachments.sort((a: any, b: any) => Number(a?.audit_sequence || 0) - Number(b?.audit_sequence || 0));
    const hasImages = attachments.length > 0 || (Array.isArray(vm?.shipmentAttachments) && vm.shipmentAttachments.length > 0);
    const imagesHtml = hasImages
      ? `
      <div class="d-print-block page-break">
        <h4>Images</h4>
        For Shipment Number: &nbsp;<span class="text-bold code">${safe(header.number)}</span>
        <hr/>
        ${attachments
        .map((a: any) => `<div style="display:inline-block; max-width:48%; margin:0 2% 20px 0"><img src="${safe(a.attachment)}" style="max-width:100%"/></div>`)
        .join('')}
      </div>`
      : '';

    return `<!DOCTYPE html>
      <html lang=""><head><meta charset="utf-8"/>${styles}<title>Shipment</title></head>
      <body>
        <div class="wrap">
          ${headerTable}
          <div style="height:20px"></div>
          ${description ? `<p>${description}</p>` : ''}
          ${tableHtml}
          ${dangerousRows || nonRegRows ? page2Html : ''}
          ${imagesHtml}
        </div>
      </body></html>`;
  }
}
